/**
 * "Modified since you last saw" (collaboration phase 4): what others changed in a project since you last left it
 * (vfs-service `GET /collab/since-last-seen`), refreshed every minute and on `collab:seen` (you marked it all seen).
 * `projectId` null: nothing is fetched. Answers `{ since, changes, files, authors, byPath, markSeen }`; `byPath` maps a
 * project path (`services/web/src/App.jsx`) to its entry `{ path, count, at, by }`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSinceLastSeen, markProjectSeen } from '../services/CollabService';

const REFRESH_MS = 60000;
const EMPTY = { since: null, changes: 0, files: [], authors: [] };

export default function useSinceLastSeen(projectId, { refreshMs = REFRESH_MS } = {}) {
  const [data, setData] = useState(EMPTY);

  const load = useCallback(async () => {
    if (!projectId) { setData(EMPTY); return; }
    try {
      const answer = await getSinceLastSeen(projectId);
      setData(answer && typeof answer === 'object' ? { ...EMPTY, ...answer } : EMPTY);
    } catch {
      // keep the last answer (an older vfs-service has none: stays empty)
    }
  }, [projectId]);

  useEffect(() => {
    setData(EMPTY);
    if (!projectId) { return undefined; }
    load();
    const timer = setInterval(load, refreshMs);
    const onSeen = (event) => { if (!event?.detail?.projectId || event.detail.projectId === projectId) { load(); } };
    window.addEventListener('collab:seen', onSeen);
    return () => {
      clearInterval(timer);
      window.removeEventListener('collab:seen', onSeen);
    };
  }, [projectId, load, refreshMs]);

  const markSeen = useCallback(async () => {
    if (!projectId) { return; }
    setData((current) => ({ ...current, changes: 0, files: [], authors: [] }));
    try {
      await markProjectSeen(projectId);
    } finally {
      window.dispatchEvent(new CustomEvent('collab:seen', { detail: { projectId } }));
    }
  }, [projectId]);

  const byPath = useMemo(() => new Map((data.files || []).map((file) => [file.path, file])), [data.files]);
  return { ...data, byPath, markSeen, reload: load };
}
